import { get } from 'lodash';
import { IReference } from '../references/IReference';
import { mapReferenceToPath } from './mapReferenceToPath';

/**
 * Riferimento materializzato, ovvero contiene anche il path assoluto.
 * Utilizzato lato React (es. `useWatch`), mentre lato Yup/validatore
 * è sufficiente `mapReferenceToPath` che è _relativo_
 */
export interface IMaterializedReference extends IReference {
  absolutePath: string;
}

export function materializeReference(
  reference: IReference,
  basePath: string,
): IMaterializedReference {
  return {
    ...reference,
    absolutePath: materializeReferencePath(basePath, reference),
  };
}

export function materializeReferences(
  references: IReference[],
  basePath: string,
): IMaterializedReference[] {
  return references.map(r => materializeReference(r, basePath));
}

/**
 * Ottiene il path completo di una reference a partire dal basePath, passando
 * da un path relativo a uno assoluto.
 * @param basePath Path diviso da punti dalla root all'elemento preso in
 *                 considerazione
 */
export function materializeReferencePath(
  basePath: string,
  reference: IReference,
) {
  const basePathTokens = basePathToTokens(basePath);
  return mapReferenceToAbsolutePath(reference, basePathTokens);
}

/**
 * Data una reference, calcola il path completo a partire dalla root.
 * @param basePathTokens Array do token del path dalla root all'elemento preso in considerazione.
 *                 Non deve contenere il nome del campo.
 */
export function mapReferenceToAbsolutePath(
  reference: IReference,
  basePathTokens: string[] = [],
) {
  const relativePath = mapReferenceToPath(reference);

  // In caso di path assoluto, lo ritorno così com'è
  if (relativePath.startsWith('$')) {
    return relativePath;
  }

  const absolutePathTokens = [...basePathTokens];

  // Se il path contiene dei "^", allora devo risalire di livello
  // altrimenti aggiungo il path al basePath
  relativePath.split('.').forEach(p => {
    if (p === '^') {
      absolutePathTokens.pop();
    } else {
      absolutePathTokens.push(p);
    }
  });

  // Calcolo il path completo riunendo i pezzi
  return absolutePathTokens.join('.');
}

/**
 * Converte un basePath in un array di token, rimuovendo l'estremo che è il nome del campo.
 */
function basePathToTokens(basePath: string) {
  const tokens = basePath.split('.');
  // Rimuovo l'estremo, che è il nome del campo
  tokens.pop();
  return tokens;
}

/**
 * Risolve una reference creando la coppia id: valore.
 * @deprecated
 */
function resolveVariable(
  reference: IReference,
  values: Record<string, any>,
  basePathTokens: string[] = [],
) {
  // Calcolo il path completo riunendo i pezzi
  const valuePath = mapReferenceToAbsolutePath(reference, basePathTokens);

  // Ottengo il valore dal form
  const value = get(values, valuePath);

  return [reference.id, value];
}
